import { AutocompleteOption } from './autocomplete';
import { FileMetadata } from './files';
import { Coordinates } from './places';
import { Language, Balcony, Rooms, Kitchen, Condition, Parking, Sauna, Storage, CarHeatingSocket, Dishwasher, LaundryMachine, Heating, BuildingType, WaterFeeBasis, SecurityDeposit, RentPeriod, PetsAllowed, AnnouncementType } from './propValues/apartments';
import { ShowingType } from './showings';
export interface CreateApartment {
    apartmentId: string;
    userId: string;
    createdAt: number;
}
/** passed on to step 0 in platform-ui */
export interface ClientContactInfo {
    name: string;
    phoneNumber: string;
}
/** will be deleted soon */
export interface ContactInfo {
    name?: string;
    email?: string;
    phoneNumber?: string;
    language?: Language;
}
export interface MainOwner extends ContactInfo {
    /** Extracted from Google Places API and saved in form*/
    coordinates?: Coordinates;
    companyName?: string;
    businessId?: string;
    /** Extracted from Google Places API */
    googlePlaceId?: string;
    /** Extracted from Google Places API */
    streetAddress?: string;
    /** address that contains streetAddress, buildingLetter and apartmentNumber */
    streetNumber?: number;
    district?: string;
    address: string;
    postalCode: string;
    postalArea: string;
    buildingLetter?: string;
    apartmentNumber?: number;
    iban?: string;
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
    role?: string;
}
export interface OtherOwner {
    name: string;
    email: string;
}
export interface ApartmentClientInformation {
    mainOwner: MainOwner;
    otherOwners?: OtherOwner[];
}
export interface AddressInfoInput {
    /** Client only prop for Google Places API. Do not save this to db. */
    autocompleteLocation: AutocompleteOption | string;
    floor?: number;
    floors?: number;
    /** Extracted from Google Places API or manually corrected */
    postalCode: string;
    /** Extracted from Google Places API or manually corrected */
    postalArea: string;
    streetNumber?: number;
    district?: string;
    buildingLetter?: string;
    apartmentNumber?: number;
}
export interface AddressInfo extends Omit<AddressInfoInput, 'autocompleteLocation'> {
    /** Extracted from Google Places API */
    streetAddress?: string;
    /** address that contains streetAddress, buildingLetter and apartmentNumber */
    address?: string;
    /** Extracted from Google Places API and saved in form*/
    googlePlaceId?: string;
    /** Extracted from Google Places API and saved in form*/
    coordinates?: Coordinates;
}
export interface BuildingInfo {
    buildingType: BuildingType;
    buildYear: number;
    storage: Storage;
}
export interface PropertyInfo {
    squareMeters: number;
    totalSquareMeters?: number;
    condition: Condition;
    rooms: Rooms;
    kitchen: Kitchen;
    balcony: Balcony;
    terrace?: boolean;
    price?: string;
    ownershipType?: OwnershipType;
}
export type OwnershipType = 'housingCompany' | 'realEstate';
export type LotOwnership = 'own' | 'rented';
export interface PriceDetails {
    salePrice?: number;
    debtFreePrice?: number;
    maintenanceFee?: number;
    financingFee?: number;
    otherCosts?: number;
}
export interface HousingCompanyDetails {
    housingCompanyName?: string;
    /** Taloyhtiön Y-tunnus muodossa 1234567-8 */
    housingCompanyBusinessId?: string;
    /** Osakenumerot HTJ-muodossa, esim. ["839-867", "A469-486"] (lomakkeella pilkkueroteltu teksti) */
    shares?: string[];
    /** Osakekirjan muoto (DIAS: sharesType) */
    sharesType?: 'UNKNOWN' | 'PAPER' | 'DIGITAL';
    /** HTJ:n osakeryhmätunnukset (16 merkkiä) — sähköisillä osakkeilla */
    osakeryhmatunnukset?: string[];
    propertyManagerContact?: string;
    lotArea?: number;
    lotOwnership?: LotOwnership;
    /** Vuokratontti: vuokra €/vuosi ja vuokrasopimuksen päättymispäivä */
    lotRentPerYear?: number;
    lotLeaseEndDate?: string;
    housingCompanySauna?: boolean;
    parkingSpaces?: string;
}
export type HeatingSystemType = 'solarHeating' | 'airWaterHeatPump' | 'districtHeating' | 'heatPump' | 'naturalGas' | 'geothermal' | 'exhaustAirHeatPump' | 'wood' | 'electricHeating' | 'oilHeating';
export interface HeatingSystem {
    heatingSystems?: HeatingSystemType[];
}
export const CheckboxPropertyKeys = {
    renovatedCheckBoxProperty: 'renovatedCheckBoxProperty' as const,
    hasElevator: 'hasElevator' as const,
    hasSaunaCheckBoxProperty: 'hasSaunaCheckBoxProperty' as const,
    hasBuiltInClosetCheckBoxProperty: 'hasBuiltInClosetCheckBoxProperty' as const,
    internetIncluded: 'internetIncluded' as const,
    newProperty: 'newProperty' as const,
    cellarStorage: 'cellarStorage' as const,
    storageRoom: 'storageRoom' as const,
    conditionInspected: 'conditionInspected' as const,
    moistureInspected: 'moistureInspected' as const,
    asbestosSurveyed: 'asbestosSurveyed' as const,
    carport: 'carport' as const,
    garage: 'garage' as const,
    fireplace: 'fireplace' as const,
    alarmSystem: 'alarmSystem' as const,
};
export interface CheckBoxProperties {
    renovatedCheckBoxProperty?: boolean;
    hasElevator?: boolean;
    hasSaunaCheckBoxProperty?: boolean;
    hasBuiltInClosetCheckBoxProperty?: boolean;
    internetIncluded?: boolean;
    newProperty?: boolean;
    cellarStorage?: boolean;
    storageRoom?: boolean;
    conditionInspected?: boolean;
    moistureInspected?: boolean;
    asbestosSurveyed?: boolean;
    carport?: boolean;
    garage?: boolean;
    fireplace?: boolean;
    alarmSystem?: boolean;
}
export interface AdditionalProperties {
    sauna: Sauna;
    parking: Parking;
    carHeatingSocket?: CarHeatingSocket; // only asked if parking answered with anything other than 'not sure' or 'no'
    dishwasher: Dishwasher;
    laundryMachine: LaundryMachine;
    heating: Heating;
    heatingDescription?: string; // if heating === 'other'
    petsAllowed: PetsAllowed;
}
export interface ApartmentInformation extends AddressInfo, PropertyInfo, BuildingInfo, AdditionalProperties, CheckBoxProperties, PriceDetails, HousingCompanyDetails, HeatingSystem {
    propertyManager?: string;
    renovations?: string;
    notes: string;
    damages?: string;
    // for SWTITCHED DATA
    price?: any;
    availableFromDate?: string;
    waterFeeBasis?: WaterFeeBasis;
    waterFee?: number;
    energyCertification?: string;
}
export type DropDownItem = {
    label: string;
    value: unknown;
};
export interface Credentials {
    email: string;
    name: string;
    password: string;
    phoneNumber: string;
    isSVAMember: boolean;
}
export interface ApartmentConfirmation {
    /** ISO date string */
    availableFromDate?: string; // if not defined, per contract
    /** ISO date string example 2020/12/24 */
    availableUntilDate?: string;
    rentPeriod: RentPeriod;
    securityDeposit: SecurityDeposit;
    securityDepositAmount?: number; // not required, if securityDeposit === 'other'
    price: number;
    waterFeeBasis: WaterFeeBasis;
    /** shown if waterFeeBasis !== 'fixed',
     * Required if waterFeeBasis === 'perTenant' or 'perApartment',
     *
     * Shown but not required if waterFeeBasis === 'metered' or 'notSure' */
    waterFee?: number;
    parkingFee?: number; // not required, only shown if parking !== 'no' or 'not sure'
    requirePriceEstimate?: boolean;
    showingType: ShowingType;
    announcementType?: AnnouncementType;
    promocode?: string;
    confirm: boolean;
    notes?: string;
    email?: string;
    name?: string;
    password?: string;
    phoneNumber?: string;
    isSVAMember?: string;
    serviceFeeCommitment?: boolean;
}
export const ApartmentStatus = {
    created: 'created' as const,
    confirmed: 'confirmed' as const,
    marketing: 'marketing' as const,
    followUp: 'followUp' as const,
    rentContractWanted: 'rentContractWanted' as const,
    rented: 'rented' as const,
    cancelled: 'cancelled' as const
};
export type Status = typeof ApartmentStatus.created | typeof ApartmentStatus.confirmed | typeof ApartmentStatus.marketing | typeof ApartmentStatus.followUp | typeof ApartmentStatus.rentContractWanted | typeof ApartmentStatus.rented | typeof ApartmentStatus.cancelled;
export interface ApartmentAdminInfo {
    adminNotes: string;
}
export interface UpdateApartmentUserIdProps {
    /** new owners userId */
    newUserId: string;
}
export interface Metadata {
    created: boolean;
    /** user email not confirmed on apartment creation */
    createdBeforeLogin?: boolean;
    /** user logs in on beforeLoginStepper confirmation step and uses addAndConfirm function */
    createdOnLogin?: boolean;
    client?: boolean;
    /** this is used to check if the user data should not be pulled from apartment.client, but from users-api */
    clientContactInfoInUsersDB?: boolean;
    information?: boolean;
    confirmed?: boolean;
    media?: boolean;
    showings?: boolean;
    marketing?: boolean;
    adminInfo?: boolean;
    showingReserved?: boolean;
    followUp?: boolean;
    userIdUpdated?: boolean;
    contractRent?: boolean;
    tenant?: boolean;
    cancelled?: boolean;
}
export interface ApartmentApplicationIds {
    contractWantedId?: string;
    oldContractWantedIds: string[];
    tenantId?: string;
    oldTenantIds: string[];
}
export interface ApartmentMediaItem {
    id: string;
    key: string;
    description?: string;
}
export type ApartmentMedia = Array<ApartmentMediaItem>;
export interface Apartment {
    apartmentId: string;
    userId: string;
    /** UNIX epoch */
    createdAt: number;
    /** UNIX epoch */
    updatedAt?: number;
    /** UNIX epoch */
    applicationsViewedAt?: number;
    /** UNIX epoch */
    statusUpdatedAt?: number;
    media?: ApartmentMedia;
    metadata: Metadata;
    client?: ApartmentClientInformation;
    information?: ApartmentInformation;
    confirmation: ApartmentConfirmation;
    oviProStatistics?: any;
    switchedData?: any;
    applicationIds?: ApartmentApplicationIds;
    adminInfo?: ApartmentAdminInfo;
    status: Status;
    code: string;
    rights?: Array<any>;
}
export interface SwitchedData {
    price?: string;
    availableFromDate?: string;
    waterFeeBasis?: WaterFeeBasis;
    waterFee?: number;
}
export interface BeforeLoginApartment {
    createdAt: number;
    metadata: Metadata;
    information?: ApartmentInformation;
    confirmation?: any;
    updatedAt?: number;
    switchedData?: SwitchedData;
    credentials?: Credentials;
}
export interface AddBeforeLoginApartment extends BeforeLoginApartment {
    userId: string;
    filesToUploadMetadata?: FileMetadata[];
}
export interface ReserveApartmentData {
    apartmentId: string;
    address: string;
    addressFull?: string;
    rooms: string;
    kitchen: string;
    price: number;
}
